import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import type { GridValidRowModel } from "@mui/x-data-grid-premium";
import React, { useContext, useState } from "react";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import BaseTable from "./BaseTable.tsx";

type AccordionTableProps<R extends GridValidRowModel> = React.ComponentProps<
  typeof BaseTable<R>
> & {
  startExpanded?: boolean;
  title: string;
  titleClass?: string;
  subtitle?: string;
  subtitleClass?: string;
  highlightNew?: boolean;
  highlightCount?: number;
  totalCount: number;
  triggerFetch?: () => void;
};

function TitleCountChips({
  title,
  titleClass,
  subtitle,
  subtitleClass,
  count,
  newCount = 0,
  showNew,
}: {
  title: string;
  titleClass?: string;
  subtitle?: string;
  subtitleClass?: string;
  count?: number;
  newCount?: number;
  showNew: boolean;
}) {
  const countChip =
    count !== undefined ? (
      <Chip label={count} size="small" />
    ) : (
      <CircularProgress size={20} />
    );
  const newCountChip = showNew && (
    <Chip
      label={newCount + " new"}
      size="small"
      sx={{ backgroundColor: "highlight.main", textTransform: "none" }}
    />
  );
  return (
    <Stack
      direction={"row"}
      justifyContent={"flex-start"}
      alignItems={"center"}
      gap={"6px"}
    >
      <Typography>
        <span className={titleClass}>{title}</span>
        {subtitle && <span className={subtitleClass}>{subtitle}</span>}
      </Typography>
      {countChip}
      {newCountChip}
    </Stack>
  );
}

export default function AccordionTable<
  R extends GridValidRowModel = GridValidRowModel,
>({
  loading = false,
  startExpanded = false,
  title,
  titleClass,
  subtitle,
  subtitleClass,
  highlightNew = true,
  highlightField,
  highlightCount,
  totalCount,
  triggerFetch,
  ...datagridProps
}: AccordionTableProps<R>) {
  const [expanded, setExpanded] = useState(startExpanded);
  const { highlightDate } = useContext(ProjectContext) || {};

  const toggleAccordion = () => {
    setExpanded(!expanded);
    // inverted check because state isn't updated yet
    if (!expanded && !!triggerFetch) {
      triggerFetch();
    }
  };

  return (
    <Accordion key={title} className={"details-table"} expanded={expanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={title + "-content"}
        id={title + "-header"}
        onClick={() => toggleAccordion()}
      >
        <TitleCountChips
          title={title}
          titleClass={titleClass}
          subtitle={subtitle}
          subtitleClass={subtitleClass}
          count={totalCount}
          newCount={highlightDate ? highlightCount : undefined}
          showNew={!!highlightDate && highlightNew}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ display: "flex", padding: 1 }}>
        <BaseTable<R>
          loading={loading}
          highlightField={highlightField}
          {...datagridProps}
        />
      </AccordionDetails>
    </Accordion>
  );
}
